import { useState, useEffect, useRef } from "react";
import "./AsoEbiModal.css";
import {
  X,
  ImageIcon,
  Plus,
  ChevronDown,
  ChevronUp,
  Check,
} from "lucide-react";
import { formatPrice } from "../../helper/utils";
import RefundCalendarModal from "../../Modals/refundCalendarModal/RefundCalendarModal";
import { formatDateForDisplay } from "../../helper/utils";

const ATTIRE_TAGS = [
  "Fila",
  "Gele",
  "Aso Ebi",
  "Event Attire",
  "Customized Round Neck",
  "Customized cap",
  "Others",
];

const normalizeTag = (tag) => (tag ?? "").trim().toLowerCase();

const isDuplicateTag = (candidate, usedTags = []) => {
  const norm = normalizeTag(candidate);
  return norm !== "" && usedTags.some((t) => normalizeTag(t) === norm);
};

const AttireTagDropdown = ({ value, onChange, usedTags = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customTag, setCustomTag] = useState("");
  const [customError, setCustomError] = useState("");
  const dropdownRef = useRef(null);

  const isCustomValue = value && !ATTIRE_TAGS.slice(0, -1).includes(value);

  useEffect(() => {
    if (isCustomValue && value) {
      setShowCustomInput(true);
      setCustomTag(value);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTagSelect = (tag) => {
    if (tag === "Others") {
      setShowCustomInput(true);
      setCustomTag("");
      setCustomError("");
      onChange("");
      setIsOpen(false);
      return;
    }

    if (isDuplicateTag(tag, usedTags)) return;

    setShowCustomInput(false);
    setCustomTag("");
    setCustomError("");
    onChange(tag);
    setIsOpen(false);
  };

  const handleCustomTagChange = (e) => {
    const raw = e.target.value;
    setCustomTag(raw);
    setCustomError("");

    const trimmed = raw.trim();

    if (trimmed === "") {
      onChange("");
      return;
    }

    if (isDuplicateTag(trimmed, usedTags)) {
      setCustomError("This tag has already been used for another image.");
      onChange("");
      return;
    }

    onChange(trimmed);
  };

  const displayValue = showCustomInput ? "Others" : value || "";

  const isTagDisabled = (tag) => {
    if (tag === "Others") return false;
    return isDuplicateTag(tag, usedTags);
  };

  const isTagSelected = (tag) => {
    if (tag === "Others") return showCustomInput;
    return value === tag;
  };

  return (
    <div className="aso-attire-tag-field">
      <label htmlFor="attire-tag-dropdown">Attire Tag</label>

      <div className="aso-attire-dropdown-wrapper" ref={dropdownRef}>
        <button
          type="button"
          id="attire-tag-dropdown"
          className={`aso-attire-dropdown-trigger ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={displayValue ? "selected" : "placeholder"}>
            {displayValue || "Select an attire tag"}
          </span>
          {isOpen ? (
            <ChevronUp size={16} className="aso-dropdown-chevron" />
          ) : (
            <ChevronDown size={16} className="aso-dropdown-chevron" />
          )}
        </button>

        {isOpen && (
          <ul
            className="aso-attire-dropdown-list"
            role="listbox"
            aria-label="Attire tag options"
          >
            {ATTIRE_TAGS.map((tag) => {
              const disabled = isTagDisabled(tag);
              const selected = isTagSelected(tag);

              return (
                <li
                  key={tag}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={disabled}
                  className={[
                    "aso-attire-dropdown-item",
                    selected ? "selected" : "",
                    disabled ? "aso-attire-dropdown-item--disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => !disabled && handleTagSelect(tag)}
                >
                  <span>{tag}</span>
                  <span className="aso-attire-item-right">
                    {disabled && !selected && (
                      <span className="aso-tag-used-badge">Used</span>
                    )}
                    {selected && (
                      <Check size={14} className="aso-dropdown-check" />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div
        className={`aso-custom-tag-wrapper${showCustomInput ? " visible" : ""}`}
      >
        <div className="aso-form-group">
          <label htmlFor="custom-attire-tag-input">Custom Attire Tag</label>
          <input
            id="custom-attire-tag-input"
            type="text"
            placeholder="Enter custom attire tag"
            className={`aso-form-input${customError ? " aso-input-error" : ""}`}
            value={customTag}
            onChange={handleCustomTagChange}
            autoFocus={showCustomInput}
          />
          {customError && (
            <p className="aso-field-error" role="alert">
              {customError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const AsoEbiModal = ({
  visible,
  onClose,
  onImageUpload,
  onSave,
  title = "Event Attire",
  subtitle = "Requirements for activating Event Attire",
  requirements = {
    title: "Requirements",
    items: [
      {
        title: "Attire Delivery: ",
        description:
          "You will be responsible for delivering the attire to guests.",
      },
      {
        title: "Refund Management: ",
        description: "You will be responsible for managing refunds.",
      },
      {
        title: "Set Refund Deadline: ",
        description: "Establish a clear refund timeline to avoid disputes.",
      },
    ],
  },
  refundPolicySection = {
    title: "Add Bank Details",
    subtitle:
      "Important: Ensure the bank account name matches the name registered on the App to avoid any payment issues.",
    editTitle: "Set Refund Policy",
  },
  initialImages = [],
  initialRefundPolicy,
  isEventAttireCompulsory: initialIsCompulsory,
  showActionButtons = true,
  preferredCurrency = "NGN",
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [refundDeadlineDate, setRefundDeadlineDate] = useState("");
  const [isEventAttireCompulsory, setIsEventAttireCompulsory] =
    useState(initialIsCompulsory);
  const [showRefundCalendarModal, setShowRefundCalendarModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [tempImageUri, setTempImageUri] = useState(null);
  const [tempPrice, setTempPrice] = useState("");
  const [tempAttireTag, setTempAttireTag] = useState("");
  const [tempFile, setTempFile] = useState(null);

  const [refundDeadlineObj, setRefundDeadlineObj] = useState(null);

  useEffect(() => {
    if (visible && initialRefundPolicy) {
      const iso = new Date(initialRefundPolicy);

      if (!isNaN(iso.getTime())) {
        const dateOnly = new Date(
          iso.getFullYear(),
          iso.getMonth(),
          iso.getDate()
        );

        setRefundDeadlineObj(dateOnly);
        setRefundDeadlineDate(
          dateOnly.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        );
      }
    }
  }, [visible, initialRefundPolicy]);

  useEffect(() => {
    if (
      visible &&
      selectedImages.length === 0 &&
      Array.isArray(initialImages) &&
      initialImages.length > 0
    ) {
      setSelectedImages(
        initialImages.map((img) => ({
          uri: img.uri || img.url || "",
          file: null,
          price: img.price ?? "",
          attireTag: img.attireTag ?? "",
        }))
      );
    }
  }, [visible, initialImages]);

  useEffect(() => {
    if (typeof initialIsCompulsory === "boolean") {
      setIsEventAttireCompulsory(initialIsCompulsory);
    }
  }, [initialIsCompulsory]);

  useEffect(() => {
    return () => {
      selectedImages.forEach((img) => {
        if (img?.uri?.startsWith("blob:")) URL.revokeObjectURL(img.uri);
      });
    };
  }, []);

  const usedTagsForCurrentSlot = selectedImages
    .filter((_, i) => i !== currentImageIndex)
    .map((img) => img.attireTag)
    .filter(Boolean);

  const handleAddImage = () => {
    const emptyIndex = selectedImages.findIndex((img) => !img?.uri);
    setCurrentImageIndex(
      emptyIndex !== -1 ? emptyIndex : selectedImages.length
    );
    setTempImageUri(null);
    setTempPrice("");
    setTempAttireTag("");
    setTempFile(null);
    setShowImageUploadModal(true);
  };

  const handleImageUpload = (file) => {
    if (file instanceof File) {
      const imageUrl = URL.createObjectURL(file);
      setTempImageUri(imageUrl);
      setTempFile(file);
    }
  };

  const handleAddImageToMain = () => {
    if (!tempImageUri || !tempFile) return;

    const trimmedTag = tempAttireTag.trim();
    const duplicate = isDuplicateTag(trimmedTag, usedTagsForCurrentSlot);
    if (trimmedTag && duplicate) return;

    const newImages = [...selectedImages];

    if (currentImageIndex >= newImages.length) {
      newImages.push({
        uri: tempImageUri,
        file: tempFile,
        price: tempPrice,
        attireTag: trimmedTag,
      });
    } else {
      newImages[currentImageIndex] = {
        uri: tempImageUri,
        file: tempFile,
        price: tempPrice,
        attireTag: trimmedTag,
      };
    }

    setSelectedImages(newImages);
    onImageUpload(newImages);
    setShowImageUploadModal(false);
    setTempImageUri(null);
    setTempFile(null);
    setTempPrice("");
    setTempAttireTag("");
  };

  const handleDeleteImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    setSelectedImages(newImages);
    onImageUpload(newImages.map((img) => img.uri).filter(Boolean));
  };

  const openUploadModal = (index) => {
    const current = selectedImages[index];
    setCurrentImageIndex(index);
    setTempImageUri(current?.uri || null);
    setTempPrice(current?.price || "");
    setTempAttireTag(current?.attireTag || "");
    setTempFile(current?.file || null);
    setShowImageUploadModal(true);
  };

  const handleSave = () => {
    const saveData = {
      asoEbiImages: selectedImages.filter((img) => img.uri || img.file),
      refundDeadlineDate: refundDeadlineDate,
      isEventAttireCompulsory,
    };
    onSave(saveData);
    onClose();
  };

  const toggleMandatory = () => {
    setIsEventAttireCompulsory(!isEventAttireCompulsory);
  };

  const handleRefundPolicySave = (selectedDate) => {
    const dateOnly = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    );

    setRefundDeadlineObj(dateOnly);

    setRefundDeadlineDate(
      dateOnly.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    );

    setShowRefundCalendarModal(false);
  };

  const renderImageSlots = () => {
    const slots = [];
    const maxInitialSlots = 4;
    const displayCount = Math.max(maxInitialSlots, selectedImages.length);

    for (let index = 0; index < displayCount; index++) {
      const item = selectedImages[index];
      slots.push(
        <div key={index} className="aso-image-item-container">
          <div
            className="aso-image-placeholder"
            onClick={() => openUploadModal(index)}
          >
            {item?.uri ? (
              <img
                src={item.uri || "/placeholder.svg"}
                alt={`Attire ${index}`}
                className="aso-uploaded-image"
              />
            ) : (
              <ImageIcon className="aso-placeholder-icon" />
            )}
          </div>
          <div className="aso-details-container">
            {item?.uri && item?.price != null && item?.price !== "" && (
              <div className="aso-price">
                {formatPrice(item.price, preferredCurrency)}
              </div>
            )}
            {item?.attireTag && (
              <div className="aso-attire-tag">
                <p>{item.attireTag}</p>
              </div>
            )}
          </div>
        </div>
      );
    }
    return slots;
  };

  if (!visible) return null;

  const addButtonBlocked =
    !tempImageUri ||
    !tempPrice ||
    (tempAttireTag.trim() !== "" &&
      isDuplicateTag(tempAttireTag.trim(), usedTagsForCurrentSlot));

  const handlePriceChange = (e) => {
    let value = e.target.value;

    value = value.replace(/[^\d.]/g, "");

    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts[1];
    }

    if (value.startsWith(".")) {
      value = "";
    }

    if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
      value = value.replace(/^0+/, "");
    }

    setTempPrice(value);
  };

  return (
    <div className="aso-modal-overlay" onClick={onClose}>
      <div className="aso-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="aso-header">
          <X className="aso-close-button" onClick={onClose} />
        </div>

        <div className="aso-scroll-container">
          <div className="aso-content">
            <h2 className="aso-title">
              {title} <span className="aso-required">*</span>
            </h2>

            <div className="aso-subtitle-container">
              <p className="aso-subtitle">{subtitle}</p>
              <div className="aso-list-container">
                {requirements.items.map((item, index) => (
                  <div key={index} className="aso-list-item-container">
                    <span className="aso-bullet">•</span>
                    <div className="aso-list-text">
                      <span className="aso-bold-text">{item.title}</span>
                      <span className="aso-list-item">{item.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="aso-mandatory-container">
              <p className="aso-mandatory-text">Event Attire Mandatory</p>
              <label className="aso-switch">
                <input
                  type="checkbox"
                  checked={isEventAttireCompulsory}
                  onChange={toggleMandatory}
                />
                <span className="aso-slider"></span>
              </label>
            </div>
          </div>

          <div className="aso-upload-section">
            <h3 className="aso-upload-title">Upload Event Attire Image</h3>
            <p className="aso-upload-description">
              Select and upload your event attire! Please select the images of
              the attire and upload them along with their corresponding prices
              and descriptions.
            </p>

            <div className="aso-images-wrapper">
              <div className="aso-images-container">
                {renderImageSlots()}
                <div
                  className="aso-add-more-container"
                  onClick={handleAddImage}
                >
                  <Plus className="aso-add-more-icon" />
                  <p className="aso-add-more-text">Add More</p>
                </div>
              </div>
            </div>

            <div className="aso-refund-policy-section">
              <h3 className="aso-refund-policy-title">Set Refund Deadline</h3>
              <p className="aso-refund-policy-description">
                Important: Select a specific date as the deadline for refunds to
                ensure transparency with your guests.
              </p>

              <div className="aso-refund-buttons-container">
                <button
                  className={`aso-refund-button ${
                    !refundDeadlineDate ? "aso-refund-button-active" : ""
                  }`}
                  onClick={() => setRefundDeadlineDate("")}
                >
                  No
                </button>
                <button
                  className={`aso-refund-button ${
                    refundDeadlineDate ? "aso-refund-button-active" : ""
                  }`}
                  onClick={() => setShowRefundCalendarModal(true)}
                >
                  Yes
                </button>
              </div>

              {refundDeadlineDate && (
                <div className="aso-refund-date-display">
                  <span className="aso-refund-label">Current deadline:</span>
                  <span className="aso-refund-value">
                    {formatDateForDisplay(refundDeadlineDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {showActionButtons && (
          <div className="aso-button-container">
            <button className="aso-back-button" onClick={onClose}>
              Back
            </button>
            <button className="aso-save-button" onClick={handleSave}>
              Save
            </button>
          </div>
        )}
      </div>

      {showImageUploadModal && (
        <div
          className="aso-image-upload-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="image-upload-modal-title"
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowImageUploadModal(false);
          }}
          onClick={() => setShowImageUploadModal(false)}
        >
          <div
            className="aso-image-upload-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="image-upload-modal-title">Upload Event Attire Image</h3>
            <p>Upload a clear image of attire and put the price.</p>

            <div className="aso-image-upload-form">
              <div className="aso-image-preview-container">
                {tempImageUri ? (
                  <div className="aso-image-preview-with-image">
                    <img
                      src={tempImageUri}
                      alt="Selected attire"
                      className="aso-preview-image"
                    />
                    <button
                      className="aso-replace-button"
                      onClick={() =>
                        document.getElementById("attire-image-upload").click()
                      }
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <div
                    className="aso-image-preview"
                    role="button"
                    tabIndex={0}
                    aria-label="Upload attire image"
                    onClick={() =>
                      document.getElementById("attire-image-upload").click()
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        document.getElementById("attire-image-upload").click();
                      }
                    }}
                  >
                    <div className="aso-image-icon">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M24 32C28.4183 32 32 28.4183 32 24C32 19.5817 28.4183 16 24 16C19.5817 16 16 19.5817 16 24C16 28.4183 19.5817 32 24 32Z"
                          stroke="#666666"
                          strokeWidth="2"
                        />
                        <path
                          d="M36 12H42V18"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M36 36H42V30"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 36H6V30"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 12H6V18"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="aso-drag-text">
                      Drag & drop files or{" "}
                      <span className="aso-browse-text">Browse</span>
                    </p>
                  </div>
                )}
                <input
                  type="file"
                  id="attire-image-upload"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleImageUpload(e.target.files[0]);
                    }
                  }}
                />
              </div>

              <AttireTagDropdown
                value={tempAttireTag}
                onChange={setTempAttireTag}
                usedTags={usedTagsForCurrentSlot}
                preferredCurrency={preferredCurrency}
              />

              <div className="aso-form-group">
                <label htmlFor="attire-price-input">Price</label>
                <div className="aso-price-input-container">
                  <div className="aso-currency-label">{preferredCurrency}</div>
                  <input
                    id="attire-price-input"
                    type="text"
                    placeholder="1,000.00"
                    className="aso-price-input"
                    value={tempPrice}
                    onChange={handlePriceChange}
                  />
                </div>
              </div>

              <div className="aso-modal-buttons">
                <button
                  className="aso-modal-back"
                  onClick={() => setShowImageUploadModal(false)}
                >
                  Back
                </button>
                <button
                  className="aso-modal-add"
                  onClick={handleAddImageToMain}
                  disabled={addButtonBlocked}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* {showRefundPolicyModal && (
        <div className="aso-refund-modal">
          <div
            className="aso-refund-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Set Refund Timeline</h3>
            <p>
              Establish clear guidelines for refunds to avoid disputes with
              guests
            </p>

            <div className="aso-refund-form">
              <div className="aso-form-group">
                <label>Timeline</label>
                <input
                  type="number"
                  placeholder="Enter number"
                  className="aso-form-input"
                />
              </div>

              <div className="aso-form-group">
                <label>Unit</label>
                <select className="aso-form-select">
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
              </div>

              <div className="aso-modal-buttons">
                <button
                  className="aso-modal-cancel"
                  onClick={() => setShowRefundPolicyModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="aso-modal-save"
                  onClick={() => handleRefundPolicySave("7", "days")} // Example values
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}

      <RefundCalendarModal
        visible={showRefundCalendarModal}
        initialDate={refundDeadlineObj}
        onClose={() => setShowRefundCalendarModal(false)}
        onSave={handleRefundPolicySave}
      />
    </div>
  );
};

export default AsoEbiModal;
